import { createSlice } from "@reduxjs/toolkit";

// Define structure based on actual Redis messages
interface TrackParameter {
  id: number;
  display: boolean;
  "site-level": Array<{
    name: string;
    value: any;
    [key: string]: any;
  }>;
  "track-level": Array<{
    name: string;
    value: any;
    [key: string]: any;
  }>;
  "non-editable": Array<any>;
  [key: string]: any;
}

// Main state type
interface TrackParametersState {
  // Store raw parameters as received
  params: {
    [trackName: string]: TrackParameter;
  };
  // Track update history for replay
  history: Array<{
    params: {
      [trackName: string]: TrackParameter;
    };
    timestamp: number;
  }>;
  // Last update time
  lastUpdate: number;
}

// Upper bound on retained history snapshots. `history` is only kept for a
// potential replay timeline; nothing reads it today, so we cap it to stop the
// array (and the per-message sort below) from growing without bound over a
// multi-hour session.
const MAX_HISTORY = 500;

const initialState: TrackParametersState = {
  params: {},
  history: [],
  lastUpdate: 0,
};

export const trackParametersSlice = createSlice({
  name: "trackParameters",
  initialState,
  reducers: {
    update: (state, action) => {
      try {
        const { params, effectiveTime } = action.payload;

        // Store the new parameters
        if (params?.items) {
          state.params = params.items;

          // Add to history for replay timeline
          state.history.push({
            params: params.items,
            timestamp: effectiveTime || Date.now(),
          });

          // Keep history sorted by timestamp
          state.history.sort((a, b) => a.timestamp - b.timestamp);

          // Bound the array: drop the oldest entries once we exceed the cap so
          // the store doesn't grow unbounded (and the sort above stays cheap).
          if (state.history.length > MAX_HISTORY) {
            state.history.splice(0, state.history.length - MAX_HISTORY);
          }

          // Update last update time
          state.lastUpdate = effectiveTime || Date.now();
        }
      } catch (error) {
        console.error("Error updating track parameters in Redux:", error);
        // Don't crash the Redux store
      }
    },
  },
});

export const { update } = trackParametersSlice.actions;
export default trackParametersSlice.reducer;
